import { useMemo } from "react";
import { ResponsiveLine } from "@nivo/line";
import { ChartTooltip } from "./ChartTooltip";
import { LineGraphProps } from "api/interfaces/chartInterface.interface";
import { useAppSelector } from "redux/hooks";
import { COLORS } from "styles/colors";
import {
  buildKeysFromSelection,
  buildNiceTicksMax5,
  buildTickValues,
  CHART_MARGIN_BOTTOM,
  CHART_MARGIN_TOP,
  findSerieItem,
  getTickValuesFormat,
} from "utils/ChartUtil";
import { calcRem } from "styles/theme";
import { GraphWrapper, GraphChartInner } from "./BarGraph";
import GridYAxis, { createOffsetLinesLayer } from "./GridYAxis";

/** Nivo 기본 라인은 숨기고 OffsetLinesLayer로 1px 그리기 */
const LINE_STROKE_WIDTH = 2;

export default function LineGraph(props: LineGraphProps) {
  const {
    items,
    rows,
    width,
    areaOpacity,
    activeOrgs,
    unit = "number",
    ...rest
  } = props;
  const theme = useAppSelector((state) => state.theme.theme);
  const tickColor =
    theme === "light" ? `${COLORS.BLACK}80` : `${COLORS.WHITE}80`;
  const LineColor =
    theme === "light" ? `${COLORS.BLACK}20` : `${COLORS.WHITE}20`;
  const visibleItems = useMemo(() => {
    const activeKeys = buildKeysFromSelection(activeOrgs, items);
    return items.filter((item) => activeKeys.includes(item.key));
  }, [activeOrgs, items]);

  const data = useMemo(() => {
    const isSingle = visibleItems.length <= 1;
    return visibleItems.map((item, index) => ({
      label: item.label,
      id: item.key + index,
      data: rows.map((row) => ({
        x: row.date ?? row.label,
        y: isSingle ? row[item.key] ?? row.value : row[item.key] ?? 0,
      })),
    }));
  }, [visibleItems, rows]);

  // 라인은 시리즈별 값(비합산) — stacked bar 합계와 Y축을 맞출 필요 없음
  const { tickValues, yMax } = useMemo(() => {
    const maxY = Math.max(
      0,
      ...data.flatMap((series: { data: { y: unknown }[] }) =>
        series.data.map((p) => Number(p.y))
      )
    );
    if (maxY === 0) {
      return { tickValues: [0, 1], yMax: 1 };
    }
    const ticks = buildNiceTicksMax5(maxY);
    const maxTickValue = Math.max(...ticks);
    return { tickValues: ticks, yMax: maxTickValue };
  }, [data]);

  const gridYTickValues = useMemo(() => {
    if (rest.gridYValues !== undefined) return rest.gridYValues;
    if (!rest.enableGridY) return undefined;
    return tickValues;
  }, [rest.gridYValues, rest.enableGridY, tickValues]);

  const yScale = useMemo(
    () => ({
      type: "linear" as const,
      min: rest.enableGridY ? 0 : undefined,
      max: rest.enableGridY ? yMax : undefined,
      stacked: false as const,
    }),
    [rest.enableGridY, yMax]
  );

  const hasVisibleSeries = visibleItems.length > 0;

  const tickXValues = useMemo(() => {
    if (!hasVisibleSeries) return [];
    return buildTickValues(
      rows.map((r) => r.date ?? r.label),
      width,
      {
        includeEdgeDates: false,
      }
    );
  }, [visibleItems.length, rows, width]);

  const sum = useMemo(() => {
    return rows.reduce((acc, curr) => acc + curr.value, 0);
  }, [rows]);

  const getSerieLineColor = useMemo(
    () => (serie: { id: string | number }) =>
      findSerieItem(serie.id, visibleItems)?.color ?? "#0284C7",
    [visibleItems]
  );

  const offsetLinesLayer = useMemo(
    () => createOffsetLinesLayer(LINE_STROKE_WIDTH),
    []
  );

  return (
    <GraphWrapper>
      {rest.enableGridY && (
        <GridYAxis
          gridYValues={gridYTickValues}
          tickYValues={tickValues}
          enableGridY={rest.enableGridY}
          unit={unit}
        />
      )}
      <GraphChartInner>
        <ResponsiveLine
          {...rest}
          data={data}
          margin={{
            top: CHART_MARGIN_TOP,
            right: 0,
            bottom: CHART_MARGIN_BOTTOM,
            left: 0,
          }}
          isInteractive
          animate
          enableCrosshair
          crosshairType="bottom-left"
          xScale={{ type: "point" }}
          yScale={yScale}
          curve="monotoneX"
          enablePoints={true}
          pointSize={7}
          pointColor={theme === "light" ? COLORS.WHITE : COLORS.BLACK}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          lineWidth={0}
          colors={getSerieLineColor}
          axisLeft={rest.axisLeft ?? null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 12,
            tickValues: tickXValues,
            format: (v) => getTickValuesFormat(tickXValues, String(v)),
            legendOffset: 1,
          }}
          axisTop={null}
          axisRight={null}
          enableGridX={false}
          enableGridY={rest.enableGridY}
          gridYValues={gridYTickValues}
          layers={[
            "grid",
            "markers",
            "areas",
            offsetLinesLayer,
            "points",
            "mesh",
            "axes",
            "legends",
          ]}
          useMesh
          tooltip={({ point }) => {
            const serie = findSerieItem(point.serieId, visibleItems);
            return (
              <ChartTooltip
                id={serie?.label}
                value={Number(point.data.y)}
                indexValue={String(point.data.xFormatted ?? point.data.x)}
                color={String(point.serieColor ?? point.color)}
                sum={sum}
                unit={unit}
              />
            );
          }}
          theme={{
            axis: {
              ticks: {
                text: { fill: tickColor, fontSize: calcRem(12) },
              },
            },
            grid: {
              line: {
                stroke: rest.theme?.grid?.line?.stroke ?? LineColor,
                strokeWidth:
                  rest.theme?.grid?.line?.strokeWidth ??
                  (rest.enableGridY ? 0.5 : 0),
              },
            },
          }}
          areaOpacity={areaOpacity}
          enableArea={!!areaOpacity}
          areaBaselineValue={areaOpacity ? -yMax * 0.01 : undefined}
        />
      </GraphChartInner>
    </GraphWrapper>
  );
}
